import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Users2, X } from "lucide-react";
import { http, formatApiError, askDelete } from "../../lib/api";

const inp = "border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function AcksModal({ i, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { http.get(`/site-instructions/${i.id}/acks`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [i.id]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[85vh] overflow-auto border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="instruction-acks-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200"><h3 className="font-bold">Confirmations — {i.title}</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="divide-y divide-slate-100">
          {items === null && <div className="p-5 text-sm text-slate-400 text-center">Chargement…</div>}
          {items?.length === 0 && <div className="p-5 text-sm text-slate-500 text-center">Aucune confirmation.</div>}
          {(items || []).map((a) => <div key={a.id} className="px-5 py-2 text-sm flex justify-between"><span>{a.agent_name}</span><span className={`text-xs ${a.version === i.version ? "text-emerald-700" : "text-slate-400"}`}>{fmt(a.at)} · v{a.version}{a.version !== i.version && " (ancienne)"}</span></div>)}
        </div>
      </div>
    </div>
  );
}

export function InstructionsAdmin({ siteId }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", body: "" });
  const [edit, setEdit] = useState(null);
  const [acks, setAcks] = useState(null);
  const load = () => http.get("/site-instructions", { params: { site_id: siteId } }).then((r) => setItems(r.data));
  useEffect(() => { if (siteId) load(); setEdit(null); setF({ title: "", body: "" }); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    try {
      if (edit) await http.put(`/site-instructions/${edit}`, { site_id: siteId, ...f }); else await http.post("/site-instructions", { site_id: siteId, ...f });
      toast.success(edit ? "Consigne modifiée : les agents devront la reconfirmer" : "Consigne publiée"); setEdit(null); setF({ title: "", body: "" }); load();
    } catch (er) { toast.error(formatApiError(er)); }
  };
  const del = async (i) => { if (askDelete(`Supprimer la consigne « ${i.title} » ?`)) { await http.delete(`/site-instructions/${i.id}`); load(); } };
  return (
    <div className="space-y-3" data-testid="instructions-admin">
      <form onSubmit={save} className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="instruction-form">
        <input data-testid="instruction-title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={`${inp} w-full`} placeholder="Titre (ex. Procédure d'ouverture du site)" />
        <textarea data-testid="instruction-body" required rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${inp} w-full`} placeholder="Texte de la consigne" />
        <div className="flex gap-2">
          <button data-testid="instruction-save" className="bg-[#0B2A7A] text-white text-sm font-semibold rounded-md px-4 py-2">{edit ? "Enregistrer" : "Publier"}</button>
          {edit && <button type="button" onClick={() => { setEdit(null); setF({ title: "", body: "" }); }} className="border border-slate-300 text-sm rounded-md px-3 py-2">Annuler</button>}
        </div>
      </form>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.length === 0 && <div className="p-5 text-sm text-slate-500 text-center">Aucune consigne sur ce site.</div>}
        {items.map((i) => (
          <div key={i.id} className="px-4 py-3 flex items-start gap-3" data-testid={`instruction-row-${i.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{i.title} <span className="text-xs text-slate-400">v{i.version}</span></div>
              <div className="text-xs text-slate-500 line-clamp-2 whitespace-pre-line">{i.body}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Mise à jour {fmt(i.updated_at)}{i.updated_by && ` par ${i.updated_by}`}</div>
            </div>
            <button data-testid={`instruction-acks-${i.id}`} onClick={() => setAcks(i)} className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 rounded-md px-2 py-1"><Users2 className="h-3.5 w-3.5" />{i.ack_count} confirmation(s)</button>
            <button data-testid={`instruction-edit-${i.id}`} onClick={() => { setEdit(i.id); setF({ title: i.title, body: i.body }); }} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`instruction-delete-${i.id}`} onClick={() => del(i)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {acks && <AcksModal i={acks} onClose={() => setAcks(null)} />}
    </div>
  );
}
